import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert, Button, CalendarDate, CheckboxGroup, DatePicker, Modal, Plus, Tag, TileGroup,
} from "@opendate-app/setlist";
import { errorText } from "../../../shared/toastHelper";
import { formatOwnValue } from "../../../shared/seriesOwnValues";
import { plural, ruleText, venueDay } from "../../SeriesEdit/updateDates";
import { ticketSetKeysFor, ticketSetsFromRows } from "../../SeriesEdit/ticketSets";

export const PREVIEW_DEBOUNCE_MS = 400;
const UNCOVERED_DATES = "Some of these dates aren't covered by a ticket set, so they can't be added. Pick dates your ticket sets cover.";
const UNDECIDED_DATES = "Some of these dates match more than one ticket set and no set wins, so they can't be added. Pick other dates.";
const LISTED_DATES = 5;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Venue days travel as "YYYY-MM-DD"; arithmetic runs in UTC so no browser zone moves them.
const addDays = (iso, days) => {
  const date = new Date(`${iso}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};
const weekdayOf = (iso) => new Date(`${iso}T12:00:00Z`).getUTCDay();
const toPickerDate = (iso) => (iso ? new Date(`${iso}T00:00:00`) : null);
const fromPickerDate = (date) => (date
  ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
  : "");
const shortDay = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const dateList = (isos) => {
  const listed = isos.slice(0, LISTED_DATES).map(shortDay).join(", ");
  return isos.length > LISTED_DATES ? `${listed} and ${isos.length - LISTED_DATES} more` : listed;
};
const firstAt = (iso, timeZone) => new Date(iso).toLocaleString("en-US", {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit", ...(timeZone ? { timeZone } : {}),
});
// Each group of new dates copies its own times, so ticket sets resolve per group.
const ticketSetKeysByGroup = (groups, sets) => groups.map((group) => ticketSetKeysFor(group.dates, group.entry_times, sets))
  .reduce((all, one) => ({
    keys: { ...all.keys, ...one.keys },
    byDate: { ...all.byDate, ...one.byDate },
    undecided: [...all.undecided, ...one.undecided].sort(),
    uncovered: [...all.uncovered, ...one.uncovered].sort(),
  }), { keys: {}, byDate: {}, undecided: [], uncovered: [] });
const money = (price, currency) => (Number(price) ? formatOwnValue(price, "money", { currency }) : "Free");

export const addDatesDefaults = (schedule, timeZone) => {
  const days = schedule.filter((row) => row.start_time).map((row) => venueDay(row.start_time, timeZone)).sort();
  const lastDay = days[days.length - 1] || venueDay(new Date().toISOString(), timeZone);
  const weekdays = [...new Set(days.map(weekdayOf))].sort();
  return {
    from: addDays(lastDay, 3),
    until: addDays(lastDay, 21),
    single: addDays(lastDay, 7),
    weekdays: weekdays.length > 0 ? weekdays : [weekdayOf(lastDay)],
  };
};

const alertText = (preview, timeZone) => {
  const count = preview.dates.length;
  const parts = [];
  const passed = preview.on_sale_passed;
  if (passed > 0) {
    parts.push(`${passed} ${plural(passed, "date's", "dates'")} on-sale time has already passed, so ${passed === 1 ? "it goes" : "they go"} on sale as soon as ${passed === 1 ? "it's" : "they're"} published.`);
  }
  preview.promo_misses.forEach((miss) => {
    const which = miss.count === count
      ? (count === 1 ? "this date" : `any of these ${count} dates`)
      : `${miss.count} of them`;
    if (miss.missing) {
      parts.push(`Promo code ${miss.code} opens or closes on ${miss.missing}, which the new dates don't have, so it won't apply to ${which}.`);
      return;
    }
    parts.push(`Promo code ${miss.code} ends ${new Date(miss.ends_at).toLocaleDateString("en-US", { month: "short", day: "numeric", ...(timeZone ? { timeZone } : {}) })}, so it won't apply to ${which}.`);
  });
  return parts.join(" ");
};

const AddDatesModal = ({
  seriesName, schedule, timeZone, currency, autoPublish, ticketSets, fetchPreview, onConfirm, onCancel,
}) => {
  const [defaults] = useState(() => addDatesDefaults(schedule, timeZone));
  const [mode, setMode] = useState("repeat");
  const [from, setFrom] = useState(defaults.from);
  const [until, setUntil] = useState(defaults.until);
  const [weekdays, setWeekdays] = useState(defaults.weekdays);
  const [single, setSingle] = useState(defaults.single);
  const [publish, setPublish] = useState(autoPublish ? "now" : "draft");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // State lags a second click in the same tick; the ref doesn't.
  const savingRef = useRef(false);
  const requestId = useRef(0);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const params = mode === "single" ? { mode, date: single } : { mode, from, until, weekdays };

  useEffect(() => {
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    const timer = setTimeout(() => {
      fetchPreview(params)
        .then((next) => {
          if (id !== requestId.current) return;
          setPreview(next);
          setPreviewError(null);
        })
        .catch((e) => {
          if (id !== requestId.current) return;
          setPreview(null);
          setPreviewError(errorText(e, "Couldn't work out these dates. Try again."));
        })
        .finally(() => { if (id === requestId.current) setLoading(false); });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [mode, from, until, single, weekdays.join(",")]);

  const dates = preview ? preview.dates : [];
  const count = dates.length;
  const sets = useMemo(() => (ticketSets.length > 0 ? ticketSetsFromRows(ticketSets) : null), [ticketSets]);
  const timeGroups = (preview && preview.time_groups) || [];
  const setKeys = sets && preview ? ticketSetKeysByGroup(timeGroups, sets) : null;
  const uncovered = Boolean(setKeys) && Object.values(setKeys.keys).some((byTime) => Object.values(byTime).some((key) => key == null));
  // A stale preview's dates aren't the ones picked, so the alerts wait for the new one.
  const unpickedAlerts = setKeys && !loading
    ? [[setKeys.undecided, UNDECIDED_DATES], [setKeys.uncovered, UNCOVERED_DATES]].filter(([isos]) => isos.length > 0)
    : [];
  const setName = (key) => (sets.find((set) => set.key === key) || {}).name;
  const setCounts = setKeys
    ? sets.map((set) => [set.name, dates.filter((date) => setKeys.byDate[date] === set.key).length]).filter(([, used]) => used > 0)
    : [];
  // With ticket sets, each date's tickets come from its set; the summary shows the first date's.
  const bySet = setKeys && preview.ticket_types_by_set;
  const ticketTypes = !preview ? [] : bySet ? (bySet[setKeys.byDate[dates[0]]] || []) : preview.ticket_types;
  const warning = preview ? alertText(preview, timeZone) : "";

  const toggleDay = (day, on) => setWeekdays((days) => (on ? [...days, day].sort() : days.filter((d) => d !== day)));

  const close = () => { if (!saving) onCancel(); };

  const confirm = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await onConfirm({ ...params, publish: publish === "now", ...(setKeys && { ticket_set_keys: JSON.stringify(setKeys.keys) }) });
    } finally {
      savingRef.current = false;
      // A successful add closes the modal before this runs.
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={close}
      width={620}
      title={`Add dates to ${seriesName}`}
      description="New dates use the series' current settings: tickets, show activities, add-ons and memberships."
      footer={(
        <>
          <Button variant="secondary" onClick={close} disabled={saving}>Cancel</Button>
          <Button variant="primary" disabled={count === 0 || uncovered || saving || loading} onClick={confirm}>
            {`Add ${count} ${plural(count, "date")}`}
          </Button>
        </>
      )}
    >
      <div className="series-add-dates-body">
        <TileGroup
          columns={2}
          value={mode}
          onChange={setMode}
          items={[
            { value: "repeat", label: "Repeating dates", icon: CalendarDate, supportingText: "Extend the run on a weekly pattern." },
            { value: "single", label: "One date", icon: Plus, supportingText: "A single extra date." },
          ]}
        />
        {mode === "repeat" ? (
          <>
            <div className="series-add-dates-range">
              <DatePicker label="From" value={toPickerDate(from)} onChange={(date) => setFrom(fromPickerDate(date))} />
              <DatePicker label="Until" value={toPickerDate(until)} onChange={(date) => setUntil(fromPickerDate(date))} />
            </div>
            <div>
              <div className="series-add-dates-label">On</div>
              <div className="series-add-dates-days">
                {WEEKDAYS.map((label, day) => (
                  <Tag
                    key={label}
                    size="md"
                    selectable
                    selected={weekdays.includes(day)}
                    onSelectedChange={(on) => toggleDay(day, on)}
                  >
                    {label}
                  </Tag>
                ))}
              </div>
            </div>
          </>
        ) : (
          <DatePicker label="Date" value={toPickerDate(single)} onChange={(date) => setSingle(fromPickerDate(date))} />
        )}
        {timeGroups.length === 1 && (
          <div className="series-add-dates-muted">{`${timeGroups[0].times} each date.`}</div>
        )}
        <div className="series-add-dates-summary">
          <div className="series-add-dates-summary-head">
            <span className="series-add-dates-summary-title">{`${count} new ${plural(count, "date")}`}</span>
            {count > 0 && (
              <span className="series-add-dates-summary-range">
                {shortDay(dates[0])}
                {count > 1 ? ` – ${shortDay(dates[count - 1])}` : ""}
              </span>
            )}
          </div>
          {preview && (
            <div className="series-add-dates-summary-grid">
              {timeGroups.length > 1 && timeGroups.map((group) => (
                <React.Fragment key={group.dates[0]}>
                  <span className="series-add-dates-muted">{dateList(group.dates)}</span>
                  <span>{group.times}</span>
                </React.Fragment>
              ))}
              {setCounts.length > 0 && (
                <>
                  <span className="series-add-dates-muted">Ticket sets</span>
                  <span>{setCounts.map(([name, used]) => `${name}: ${used} ${plural(used, "date")}`).join(" · ")}</span>
                </>
              )}
              <span className="series-add-dates-muted">Tickets</span>
              <span>
                {ticketTypes.map((type) => `${type.name} ${money(type.price, currency)}`).join(" · ")}
                {setCounts.length > 1 && <span className="series-add-dates-faint">{` · ${setName(setKeys.byDate[dates[0]])}, the first date's set`}</span>}
              </span>
              <span className="series-add-dates-muted">Capacity</span>
              <span>{`${Number(preview.capacity || 0).toLocaleString("en-US")} per date`}</span>
              {preview.show_details.slice(0, 2).map((detail) => (
                <React.Fragment key={detail.name}>
                  <span className="series-add-dates-muted">{detail.name}</span>
                  <span>
                    {ruleText(detail, timeZone)}
                    {detail.offset_minutes !== null && detail.offset_minutes !== undefined ? " each date" : ""}
                    {count > 0 && <span className="series-add-dates-faint">{` · first is ${firstAt(detail.first_at, timeZone)}`}</span>}
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
        {previewError && <Alert color="error" title={previewError} />}
        {unpickedAlerts.map(([isos, title]) => <Alert key={title} color="error" title={title} description={dateList(isos)} />)}
        {warning && <Alert color="warning" title="Before you add these" description={warning} />}
        <div>
          <div className="series-add-dates-label" id="series-add-dates-publish-label">When they're added</div>
          <CheckboxGroup
            type="radio"
            aria-labelledby="series-add-dates-publish-label"
            value={publish}
            onChange={setPublish}
            items={[
              {
                value: "now",
                label: "Publish right away",
                supportingText: autoPublish ? "This series auto-publishes new dates." : "Visible on your website and widget now.",
              },
              { value: "draft", label: "Save as drafts", supportingText: "Review them first and publish from the events table." },
            ]}
          />
        </div>
      </div>
    </Modal>
  );
};

AddDatesModal.propTypes = {
  seriesName: PropTypes.string.isRequired,
  schedule: PropTypes.arrayOf(PropTypes.shape({ start_time: PropTypes.string })).isRequired,
  timeZone: PropTypes.string,
  currency: PropTypes.string,
  autoPublish: PropTypes.bool,
  ticketSets: PropTypes.arrayOf(PropTypes.object),
  fetchPreview: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

AddDatesModal.defaultProps = {
  timeZone: null,
  currency: "USD",
  autoPublish: true,
  ticketSets: [],
};

export default AddDatesModal;
